import { useEffect, useState, useCallback, useMemo } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useJobsStore, Result, Job } from '../store/jobs'
import { getJob, listResults, updateResult, scheduleResult, updateJob as updateJobApi } from '../api/client'
import StoryPreview from '../components/StoryPreview'
import Layout from '../components/Layout'
import SearchFilter from '../components/SearchFilter'
import BookmarkToggle from '../components/BookmarkToggle'
import RefreshButton from '../components/RefreshButton'
import LoadingButton from '../components/LoadingButton'
import ErrorMessage from '../components/ErrorMessage'
import CopyButton from '../components/CopyButton'

export default function JobResults() {
  const navigate = useNavigate()
  const { jobId } = useParams<{ jobId: string }>()
  const { currentJob, setCurrentJob, updateJob: updateJobInStore, results, setResults, loading, setLoading, error, setError, updateResult: updateResultInStore } = useJobsStore()
  const [selectedResult, setSelectedResult] = useState<Result | null>(null)
  const [showScheduleModal, setShowScheduleModal] = useState(false)
  const [refreshKey, setRefreshKey] = useState(0)

  // Editable job fields
  const [editingTitle, setEditingTitle] = useState(false)
  const [editingNotes, setEditingNotes] = useState(false)
  const [titleValue, setTitleValue] = useState('')
  const [notesValue, setNotesValue] = useState('')
  const [savingJob, setSavingJob] = useState(false)

  // Filters
  const [search, setSearch] = useState('')
  const [bookmarkedOnly, setBookmarkedOnly] = useState(false)
  const [matchScoreMin, setMatchScoreMin] = useState(0)

  // Pagination
  const [currentPage, setCurrentPage] = useState(0)
  const ITEMS_PER_PAGE = 25

  useEffect(() => {
    if (jobId) {
      loadJobAndResults()
    }
  }, [jobId, refreshKey])

  useEffect(() => {
    if (currentJob) {
      setTitleValue(currentJob.title || '')
      setNotesValue(currentJob.notes || '')
    }
  }, [currentJob])

  async function loadJobAndResults() {
    setLoading(true)
    setError(null)

    try {
      const [jobData, resultsData] = await Promise.all([
        getJob(jobId!),
        listResults(jobId!, { limit: 100 }),
      ])
      setCurrentJob(jobData as Job)
      setResults(resultsData.results as Result[])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load job')
    } finally {
      setLoading(false)
    }
  }

  // Filter results
  const filteredResults = useMemo(() => {
    return results.filter((result) => {
      if (bookmarkedOnly && !result.bookmarked) return false

      if (search) {
        const searchLower = search.toLowerCase()
        const matchesCaption = result.caption?.toLowerCase().includes(searchLower)
        const matchesNotes = result.user_notes?.toLowerCase().includes(searchLower)
        const matchesAiNotes = result.ai_notes?.toLowerCase().includes(searchLower)
        if (!matchesCaption && !matchesNotes && !matchesAiNotes) return false
      }

      if (matchScoreMin > 0 && (result.match_score || 0) < matchScoreMin) return false

      return true
    })
  }, [results, bookmarkedOnly, search, matchScoreMin])

  // Paginate results
  const paginatedResults = useMemo(() => {
    const start = currentPage * ITEMS_PER_PAGE
    return filteredResults.slice(start, start + ITEMS_PER_PAGE)
  }, [filteredResults, currentPage])

  const totalPages = Math.ceil(filteredResults.length / ITEMS_PER_PAGE)

  async function handleApprove(result: Result) {
    try {
      await updateResult(jobId!, result.result_id, { approved: true })
      updateResultInStore(result.result_id, { approved: true, status: 'approved' })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to approve')
    }
  }

  async function handleReject(result: Result) {
    try {
      await updateResult(jobId!, result.result_id, { approved: false })
      updateResultInStore(result.result_id, { approved: false, status: 'rejected' })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reject')
    }
  }

  async function handleSchedule(result: Result, data: {
    scheduled_datetime: string
    on_schedule_email?: string
    instruction_text?: string
  }) {
    try {
      await scheduleResult(jobId!, result.result_id, data)
      updateResultInStore(result.result_id, { status: 'scheduled', scheduled_datetime: data.scheduled_datetime })
      setShowScheduleModal(false)
      setSelectedResult(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to schedule')
    }
  }

  const handleToggleBookmark = useCallback(async () => {
    if (!currentJob) return
    const newBookmarked = !currentJob.bookmarked
    updateJobInStore(currentJob.job_id, { bookmarked: newBookmarked })
    try {
      await updateJobApi(currentJob.job_id, { bookmarked: newBookmarked })
    } catch (err) {
      updateJobInStore(currentJob.job_id, { bookmarked: currentJob.bookmarked })
    }
  }, [currentJob, updateJobInStore])

  const handleSaveTitle = useCallback(async () => {
    if (!currentJob) return
    setSavingJob(true)
    try {
      await updateJobApi(currentJob.job_id, { title: titleValue })
      updateJobInStore(currentJob.job_id, { title: titleValue })
      setEditingTitle(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save title')
    } finally {
      setSavingJob(false)
    }
  }, [currentJob, titleValue, updateJobInStore])

  const handleSaveNotes = useCallback(async () => {
    if (!currentJob) return
    setSavingJob(true)
    try {
      await updateJobApi(currentJob.job_id, { notes: notesValue })
      updateJobInStore(currentJob.job_id, { notes: notesValue })
      setEditingNotes(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save notes')
    } finally {
      setSavingJob(false)
    }
  }, [currentJob, notesValue, updateJobInStore])

  function getStatusBadge(status: string) {
    const styles: Record<string, string> = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-gray-100 text-gray-500',
      scheduled: 'bg-blue-100 text-blue-800',
      posted: 'bg-purple-100 text-purple-800',
    }
    return styles[status] || 'bg-gray-100 text-gray-800'
  }

  function getCardStyle(status: string) {
    if (status === 'rejected') return 'opacity-50'
    if (status === 'approved') return 'ring-2 ring-green-400 ring-offset-1'
    return ''
  }

  function formatScheduledDate(datetime: string) {
    const date = new Date(datetime)
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }

  // Show loading spinner if actively loading OR if we haven't attempted to load yet
  if (loading || (!currentJob && !error)) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        </div>
      </Layout>
    )
  }

  if (!currentJob) {
    return (
      <Layout>
        <div className="max-w-6xl mx-auto px-4 py-8">
          <p className="text-red-600">{error || 'Job not found'}</p>
          <Link to="/jobs" className="text-indigo-600 hover:underline mt-4 inline-block">
            Back to Jobs
          </Link>
        </div>
      </Layout>
    )
  }

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-6">
          <Link to="/jobs" className="text-indigo-600 hover:underline text-sm mb-2 inline-block">
            Back to Jobs
          </Link>

          <div className="flex justify-between items-start">
            <div className="flex-1 mr-4">
              {/* Editable Title */}
              <div className="flex items-center gap-2">
                {editingTitle ? (
                  <div className="flex items-center gap-2 flex-1">
                    <input
                      type="text"
                      value={titleValue}
                      onChange={(e) => setTitleValue(e.target.value)}
                      className="text-2xl font-bold text-gray-900 border border-gray-300 rounded px-2 py-1 flex-1"
                      autoFocus
                    />
                    <LoadingButton
                      size="sm"
                      variant="primary"
                      loading={savingJob}
                      onClick={handleSaveTitle}
                    >
                      Save
                    </LoadingButton>
                    <button
                      onClick={() => {
                        setTitleValue(currentJob.title || '')
                        setEditingTitle(false)
                      }}
                      className="text-sm text-gray-500 hover:text-gray-700"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <>
                    <h1 className="text-2xl font-bold text-gray-900">
                      {currentJob.title || 'Untitled Job'}
                    </h1>
                    <button
                      onClick={() => setEditingTitle(true)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                      </svg>
                    </button>
                    <button
                      onClick={handleToggleBookmark}
                      className="text-gray-400 hover:text-amber-500 transition-colors"
                    >
                      <svg
                        className={`w-5 h-5 ${currentJob.bookmarked ? 'fill-amber-500 text-amber-500' : ''}`}
                        fill={currentJob.bookmarked ? 'currentColor' : 'none'}
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"
                        />
                      </svg>
                    </button>
                  </>
                )}
              </div>

              {/* Job ID with copy */}
              <div className="flex items-center gap-1 text-xs text-gray-400 mt-1">
                <span className="font-mono">{jobId?.slice(0, 8)}...</span>
                <CopyButton text={jobId || ''} size="sm" />
              </div>
            </div>

            <div className="flex gap-3">
              {currentJob.nocodb_results_view_url && (
                <a
                  href={currentJob.nocodb_results_view_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                >
                  View Database
                </a>
              )}
              <RefreshButton
                onClick={() => setRefreshKey((k) => k + 1)}
                loading={loading}
                size="md"
              />
            </div>
          </div>

          {/* Editable Notes */}
          <div className="mt-3">
            {editingNotes ? (
              <div className="flex items-start gap-2">
                <textarea
                  value={notesValue}
                  onChange={(e) => setNotesValue(e.target.value)}
                  rows={2}
                  className="flex-1 text-sm text-gray-600 border border-gray-300 rounded px-2 py-1"
                  placeholder="Add notes..."
                />
                <LoadingButton
                  size="sm"
                  variant="primary"
                  loading={savingJob}
                  onClick={handleSaveNotes}
                >
                  Save
                </LoadingButton>
                <button
                  onClick={() => {
                    setNotesValue(currentJob.notes || '')
                    setEditingNotes(false)
                  }}
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <div
                onClick={() => setEditingNotes(true)}
                className="text-sm text-gray-600 cursor-pointer hover:bg-gray-50 rounded p-1 -m-1"
              >
                {currentJob.notes || <span className="text-gray-400 italic">Click to add notes...</span>}
              </div>
            )}
          </div>

          {/* Job Stats */}
          <div className="mt-4 flex flex-wrap gap-4 text-sm">
            <span className={`px-2 py-1 rounded-full ${getStatusBadge(currentJob.status)}`}>
              {currentJob.status}
            </span>
            <span className="text-gray-600">
              {currentJob.completed_results}/{currentJob.total_results} results
            </span>
            {currentJob.approved_count !== undefined && (
              <span className="text-green-600 font-medium">
                {currentJob.approved_count} approved
              </span>
            )}
            <span className="text-gray-600">
              {currentJob.media_mode.replace(/_/g, ' ')}
            </span>
            <span className="text-gray-600">
              {currentJob.credits_charged.toFixed(2)} credits
            </span>
          </div>
        </div>

        {/* Job Config Details */}
        <details className="mb-6 bg-white rounded-lg border border-gray-200">
          <summary className="px-4 py-3 cursor-pointer text-sm font-medium text-gray-700 hover:bg-gray-50">
            Job Configuration
          </summary>
          <div className="px-4 pb-4 space-y-3">
            {/* Content Prompt */}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Content Prompt</label>
              <div className="flex items-start gap-2">
                <textarea
                  readOnly
                  value={currentJob.content_prompt}
                  rows={3}
                  className="flex-1 text-sm bg-gray-50 border border-gray-200 rounded px-2 py-1"
                />
                <CopyButton text={currentJob.content_prompt} size="sm" />
              </div>
            </div>

            {currentJob.filter_prompt && (
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Filter Prompt</label>
                <div className="flex items-start gap-2">
                  <textarea
                    readOnly
                    value={currentJob.filter_prompt}
                    rows={2}
                    className="flex-1 text-sm bg-gray-50 border border-gray-200 rounded px-2 py-1"
                  />
                  <CopyButton text={currentJob.filter_prompt} size="sm" />
                </div>
              </div>
            )}

            {currentJob.schedule_prompt && (
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Schedule Prompt</label>
                <div className="flex items-start gap-2">
                  <textarea
                    readOnly
                    value={currentJob.schedule_prompt}
                    rows={2}
                    className="flex-1 text-sm bg-gray-50 border border-gray-200 rounded px-2 py-1"
                  />
                  <CopyButton text={currentJob.schedule_prompt} size="sm" />
                </div>
              </div>
            )}

            {/* Full JSON */}
            <details className="mt-4">
              <summary className="cursor-pointer text-xs text-gray-500 hover:text-gray-700">
                View Full JSON
              </summary>
              <div className="mt-2 flex items-start gap-2">
                <pre className="flex-1 text-xs bg-gray-100 p-2 rounded overflow-auto max-h-48">
                  {JSON.stringify({
                    job_id: currentJob.job_id,
                    content_prompt: currentJob.content_prompt,
                    output_quantity: currentJob.output_quantity,
                    media_mode: currentJob.media_mode,
                    publish_mode: currentJob.publish_mode,
                    filter_prompt: currentJob.filter_prompt,
                    schedule_prompt: currentJob.schedule_prompt,
                    tracer: currentJob.tracer,
                    inbox_tracer: currentJob.inbox_tracer,
                    on_job_finish_webhook: currentJob.on_job_finish_webhook,
                    on_job_finish_email: currentJob.on_job_finish_email,
                    on_schedule_webhook_default: currentJob.on_schedule_webhook_default,
                    on_schedule_email_default: currentJob.on_schedule_email_default,
                  }, null, 2)}
                </pre>
                <CopyButton
                  text={JSON.stringify(currentJob, null, 2)}
                  size="sm"
                />
              </div>
            </details>
          </div>
        </details>

        {error && (
          <ErrorMessage
            message={error}
            className="mb-6"
            onDismiss={() => setError(null)}
          />
        )}

        {/* Filters */}
        <div className="flex flex-wrap gap-4 mb-6 items-center">
          <SearchFilter
            value={search}
            onChange={setSearch}
            placeholder="Search by caption, notes..."
            className="flex-1 max-w-sm"
          />
          <BookmarkToggle
            active={bookmarkedOnly}
            onChange={setBookmarkedOnly}
          />
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">Min Score:</label>
            <input
              type="range"
              min="0"
              max="100"
              value={matchScoreMin}
              onChange={(e) => setMatchScoreMin(Number(e.target.value))}
              className="w-24"
            />
            <span className="text-sm text-gray-600 w-8">{matchScoreMin}</span>
          </div>
        </div>

        {/* Results Grid */}
        {results.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
            <p className="text-gray-600">
              {currentJob.status === 'processing'
                ? 'Results are being generated...'
                : 'No results yet'}
            </p>
          </div>
        ) : filteredResults.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
            <p className="text-gray-600">No results match your filters.</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
              {paginatedResults.map((result) => (
                <ResultCard
                  key={result.result_id}
                  result={result}
                  jobId={jobId!}
                  onApprove={handleApprove}
                  onReject={handleReject}
                  onSchedule={(r) => {
                    setSelectedResult(r)
                    setShowScheduleModal(true)
                  }}
                  onNavigateCalendar={(date) => navigate(`/calendar?date=${date}`)}
                  getStatusBadge={getStatusBadge}
                  getCardStyle={getCardStyle}
                  formatScheduledDate={formatScheduledDate}
                  updateResultInStore={updateResultInStore}
                  setError={setError}
                />
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-between items-center mt-4">
                <div className="text-sm text-gray-600">
                  Showing {currentPage * ITEMS_PER_PAGE + 1}-{Math.min((currentPage + 1) * ITEMS_PER_PAGE, filteredResults.length)} of {filteredResults.length}
                </div>
                <div className="flex gap-2">
                  <LoadingButton
                    variant="secondary"
                    size="sm"
                    onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
                    disabled={currentPage === 0}
                  >
                    Previous
                  </LoadingButton>
                  <LoadingButton
                    variant="secondary"
                    size="sm"
                    onClick={() => setCurrentPage((p) => Math.min(totalPages - 1, p + 1))}
                    disabled={currentPage >= totalPages - 1}
                  >
                    Next
                  </LoadingButton>
                </div>
              </div>
            )}
          </>
        )}

        {/* Schedule Modal */}
        {showScheduleModal && selectedResult && (
          <ScheduleModal
            result={selectedResult}
            job={currentJob}
            onClose={() => {
              setShowScheduleModal(false)
              setSelectedResult(null)
            }}
            onSchedule={(data) => handleSchedule(selectedResult, data)}
          />
        )}
      </div>
    </Layout>
  )
}

function SchedulingSettings({
  result,
  jobId,
  updateResultInStore,
  setError,
}: {
  result: Result
  jobId: string
  updateResultInStore: (id: string, updates: Partial<Result>) => void
  setError: (e: string | null) => void
}) {
  const [editingEmail, setEditingEmail] = useState(false)
  const [editingWebhook, setEditingWebhook] = useState(false)
  const [editingInstructions, setEditingInstructions] = useState(false)
  const [emailValue, setEmailValue] = useState(result.on_schedule_email || '')
  const [webhookValue, setWebhookValue] = useState(result.on_schedule_webhook || '')
  const [instructionsValue, setInstructionsValue] = useState(result.instruction_text || '')
  const [saving, setSaving] = useState(false)

  const handleSaveEmail = async () => {
    setSaving(true)
    try {
      await updateResult(jobId, result.result_id, { on_schedule_email: emailValue || undefined })
      updateResultInStore(result.result_id, { on_schedule_email: emailValue || undefined })
      setEditingEmail(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save email')
    } finally {
      setSaving(false)
    }
  }

  const handleSaveWebhook = async () => {
    setSaving(true)
    try {
      await updateResult(jobId, result.result_id, { on_schedule_webhook: webhookValue || undefined })
      updateResultInStore(result.result_id, { on_schedule_webhook: webhookValue || undefined })
      setEditingWebhook(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save webhook')
    } finally {
      setSaving(false)
    }
  }

  const handleSaveInstructions = async () => {
    setSaving(true)
    try {
      await updateResult(jobId, result.result_id, { instruction_text: instructionsValue || undefined })
      updateResultInStore(result.result_id, { instruction_text: instructionsValue || undefined })
      setEditingInstructions(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save instructions')
    } finally {
      setSaving(false)
    }
  }

  return (
    <details className="bg-gray-50 rounded">
      <summary className="px-2 py-1 cursor-pointer hover:bg-gray-100 font-medium text-gray-700">
        Scheduling Settings
      </summary>
      <div className="px-2 pb-2 space-y-2">
        {/* Schedule Email */}
        <div>
          <label className="block text-[10px] font-medium text-gray-500 mb-0.5">Notify Email</label>
          {editingEmail ? (
            <div className="space-y-1">
              <input
                type="email"
                value={emailValue}
                onChange={(e) => setEmailValue(e.target.value)}
                placeholder="volunteer@example.com"
                className="w-full text-xs border border-gray-300 rounded px-2 py-1"
                autoFocus
              />
              <div className="flex gap-1">
                <button
                  onClick={handleSaveEmail}
                  disabled={saving}
                  className="px-2 py-0.5 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50"
                >
                  {saving ? '...' : 'Save'}
                </button>
                <button
                  onClick={() => {
                    setEmailValue(result.on_schedule_email || '')
                    setEditingEmail(false)
                  }}
                  className="px-2 py-0.5 text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div
              onClick={() => setEditingEmail(true)}
              className="text-gray-600 cursor-pointer hover:bg-white rounded p-1 min-h-[1.5rem] text-xs"
            >
              {result.on_schedule_email || <span className="italic text-gray-400">Click to set email...</span>}
            </div>
          )}
        </div>

        {/* Schedule Webhook */}
        <div>
          <label className="block text-[10px] font-medium text-gray-500 mb-0.5">Webhook URL</label>
          {editingWebhook ? (
            <div className="space-y-1">
              <input
                type="url"
                value={webhookValue}
                onChange={(e) => setWebhookValue(e.target.value)}
                placeholder="https://example.com/webhook"
                className="w-full text-xs border border-gray-300 rounded px-2 py-1"
                autoFocus
              />
              <div className="flex gap-1">
                <button
                  onClick={handleSaveWebhook}
                  disabled={saving}
                  className="px-2 py-0.5 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50"
                >
                  {saving ? '...' : 'Save'}
                </button>
                <button
                  onClick={() => {
                    setWebhookValue(result.on_schedule_webhook || '')
                    setEditingWebhook(false)
                  }}
                  className="px-2 py-0.5 text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div
              onClick={() => setEditingWebhook(true)}
              className="text-gray-600 cursor-pointer hover:bg-white rounded p-1 min-h-[1.5rem] text-xs truncate"
            >
              {result.on_schedule_webhook || <span className="italic text-gray-400">Click to set webhook...</span>}
            </div>
          )}
        </div>

        {/* Instructions */}
        <div>
          <label className="block text-[10px] font-medium text-gray-500 mb-0.5">Instructions</label>
          {editingInstructions ? (
            <div className="space-y-1">
              <textarea
                value={instructionsValue}
                onChange={(e) => setInstructionsValue(e.target.value)}
                rows={2}
                placeholder="Instructions for volunteer..."
                className="w-full text-xs border border-gray-300 rounded px-2 py-1"
                autoFocus
              />
              <div className="flex gap-1">
                <button
                  onClick={handleSaveInstructions}
                  disabled={saving}
                  className="px-2 py-0.5 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50"
                >
                  {saving ? '...' : 'Save'}
                </button>
                <button
                  onClick={() => {
                    setInstructionsValue(result.instruction_text || '')
                    setEditingInstructions(false)
                  }}
                  className="px-2 py-0.5 text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div
              onClick={() => setEditingInstructions(true)}
              className="text-gray-600 cursor-pointer hover:bg-white rounded p-1 min-h-[1.5rem] text-xs"
            >
              {result.instruction_text || <span className="italic text-gray-400">Click to add instructions...</span>}
            </div>
          )}
        </div>
      </div>
    </details>
  )
}

function ResultCard({
  result,
  jobId,
  onApprove,
  onReject,
  onSchedule,
  onNavigateCalendar,
  getStatusBadge,
  getCardStyle,
  formatScheduledDate,
  updateResultInStore,
  setError,
}: {
  result: Result
  jobId: string
  onApprove: (r: Result) => void
  onReject: (r: Result) => void
  onSchedule: (r: Result) => void
  onNavigateCalendar: (date: string) => void
  getStatusBadge: (status: string) => string
  getCardStyle: (status: string) => string
  formatScheduledDate: (datetime: string) => string
  updateResultInStore: (id: string, updates: Partial<Result>) => void
  setError: (e: string | null) => void
}) {
  const [editingCaption, setEditingCaption] = useState(false)
  const [editingHashtags, setEditingHashtags] = useState(false)
  const [editingNotes, setEditingNotes] = useState(false)
  const [captionValue, setCaptionValue] = useState(result.caption || '')
  const [hashtagsValue, setHashtagsValue] = useState(result.hashtags?.join(' ') || '')
  const [notesValue, setNotesValue] = useState(result.user_notes || '')
  const [saving, setSaving] = useState(false)
  const [actionLoading, setActionLoading] = useState<'approve' | 'reject' | null>(null)

  const handleSaveCaption = async () => {
    setSaving(true)
    try {
      await updateResult(jobId, result.result_id, { caption: captionValue })
      updateResultInStore(result.result_id, { caption: captionValue })
      setEditingCaption(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save caption')
    } finally {
      setSaving(false)
    }
  }

  const handleSaveHashtags = async () => {
    setSaving(true)
    try {
      const hashtags = hashtagsValue.split(/\s+/).filter((h) => h.startsWith('#') || h.length > 0).map((h) => h.startsWith('#') ? h : `#${h}`)
      await updateResult(jobId, result.result_id, { hashtags })
      updateResultInStore(result.result_id, { hashtags })
      setEditingHashtags(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save hashtags')
    } finally {
      setSaving(false)
    }
  }

  const handleSaveNotes = async () => {
    setSaving(true)
    try {
      await updateResult(jobId, result.result_id, { user_notes: notesValue })
      updateResultInStore(result.result_id, { user_notes: notesValue })
      setEditingNotes(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save notes')
    } finally {
      setSaving(false)
    }
  }

  const handleToggleBookmark = async () => {
    try {
      const newBookmarked = !result.bookmarked
      updateResultInStore(result.result_id, { bookmarked: newBookmarked })
      await updateResult(jobId, result.result_id, { bookmarked: newBookmarked })
    } catch (err) {
      updateResultInStore(result.result_id, { bookmarked: result.bookmarked })
      setError(err instanceof Error ? err.message : 'Failed to update bookmark')
    }
  }

  const handleApproveClick = async () => {
    setActionLoading('approve')
    try {
      await onApprove(result)
    } finally {
      setActionLoading(null)
    }
  }

  const handleRejectClick = async () => {
    setActionLoading('reject')
    try {
      await onReject(result)
    } finally {
      setActionLoading(null)
    }
  }

  return (
    <div className={`bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden ${getCardStyle(result.status)}`}>
      {/* Story Preview */}
      <div className="aspect-[9/16] bg-gray-100 relative">
        <StoryPreview result={result} />
        <div className="absolute top-1 right-1 flex gap-1">
          <button
            onClick={handleToggleBookmark}
            className="p-0.5 rounded bg-black/30 hover:bg-black/50"
          >
            <svg
              className={`w-3 h-3 ${result.bookmarked ? 'fill-amber-400 text-amber-400' : 'text-white'}`}
              fill={result.bookmarked ? 'currentColor' : 'none'}
              stroke="currentColor"
              viewBox="0 0 24 24"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
            </svg>
          </button>
          <span className={`px-1 py-0.5 text-[9px] font-medium rounded ${getStatusBadge(result.status)}`}>
            {result.status}
          </span>
        </div>
        {result.match_score !== undefined && (
          <div className="absolute bottom-1 left-1">
            <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-black/60 text-white">
              {result.match_score}%
            </span>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="p-1.5 border-b border-gray-100">
        {result.status === 'pending' && (
          <div className="flex gap-1">
            <button
              onClick={handleApproveClick}
              disabled={actionLoading !== null}
              className="flex-1 py-1.5 bg-green-600 text-white text-xs font-medium rounded hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-1"
            >
              {actionLoading === 'approve' ? (
                <span className="animate-spin h-3 w-3 border-2 border-white border-t-transparent rounded-full" />
              ) : (
                <>✓ Approve</>
              )}
            </button>
            <button
              onClick={handleRejectClick}
              disabled={actionLoading !== null}
              className="flex-1 py-1.5 bg-red-600 text-white text-xs font-medium rounded hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-1"
            >
              {actionLoading === 'reject' ? (
                <span className="animate-spin h-3 w-3 border-2 border-white border-t-transparent rounded-full" />
              ) : (
                <>✗ Reject</>
              )}
            </button>
          </div>
        )}

        {result.status === 'approved' && (
          <button
            onClick={() => onSchedule(result)}
            className="w-full py-1.5 bg-indigo-600 text-white text-xs font-medium rounded hover:bg-indigo-700"
          >
            Schedule Post
          </button>
        )}

        {result.status === 'scheduled' && result.scheduled_datetime && (
          <button
            onClick={() => {
              const date = new Date(result.scheduled_datetime!)
              onNavigateCalendar(date.toISOString().slice(0, 10))
            }}
            className="w-full py-1.5 bg-blue-100 text-blue-700 text-xs font-medium rounded hover:bg-blue-200"
          >
            Scheduled: {formatScheduledDate(result.scheduled_datetime)}
          </button>
        )}

        {result.status === 'rejected' && (
          <div className="text-xs text-gray-400 text-center py-1">
            Declined
          </div>
        )}
      </div>

      {/* Expandable Details */}
      <details className="text-xs group">
        <summary className="px-2 py-1.5 cursor-pointer hover:bg-gray-50 text-gray-600 font-medium flex items-center gap-1 list-none [&::-webkit-details-marker]:hidden">
          <svg className="w-3 h-3 transition-transform group-open:rotate-90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          Details
        </summary>
        <div className="px-2 pb-2 space-y-2">
          {/* Caption */}
          <details className="bg-gray-50 rounded" open>
            <summary className="px-2 py-1 cursor-pointer hover:bg-gray-100 font-medium text-gray-700">
              Caption
            </summary>
            <div className="px-2 pb-2">
              {editingCaption ? (
                <div className="space-y-1">
                  <textarea
                    value={captionValue}
                    onChange={(e) => setCaptionValue(e.target.value)}
                    rows={3}
                    className="w-full text-xs border border-gray-300 rounded px-2 py-1"
                    autoFocus
                  />
                  <div className="flex gap-1">
                    <button
                      onClick={handleSaveCaption}
                      disabled={saving}
                      className="px-2 py-0.5 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50"
                    >
                      {saving ? '...' : 'Save'}
                    </button>
                    <button
                      onClick={() => {
                        setCaptionValue(result.caption || '')
                        setEditingCaption(false)
                      }}
                      className="px-2 py-0.5 text-gray-600 hover:text-gray-800"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  onClick={() => setEditingCaption(true)}
                  className="text-gray-600 cursor-pointer hover:bg-white rounded p-1 min-h-[2rem]"
                >
                  {result.caption || <span className="italic text-gray-400">Click to add caption...</span>}
                </div>
              )}
            </div>
          </details>

          {/* Hashtags */}
          <details className="bg-gray-50 rounded">
            <summary className="px-2 py-1 cursor-pointer hover:bg-gray-100 font-medium text-gray-700">
              Hashtags ({result.hashtags?.length || 0})
            </summary>
            <div className="px-2 pb-2">
              {editingHashtags ? (
                <div className="space-y-1">
                  <textarea
                    value={hashtagsValue}
                    onChange={(e) => setHashtagsValue(e.target.value)}
                    rows={2}
                    placeholder="#hashtag1 #hashtag2"
                    className="w-full text-xs border border-gray-300 rounded px-2 py-1"
                    autoFocus
                  />
                  <div className="flex gap-1">
                    <button
                      onClick={handleSaveHashtags}
                      disabled={saving}
                      className="px-2 py-0.5 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50"
                    >
                      {saving ? '...' : 'Save'}
                    </button>
                    <button
                      onClick={() => {
                        setHashtagsValue(result.hashtags?.join(' ') || '')
                        setEditingHashtags(false)
                      }}
                      className="px-2 py-0.5 text-gray-600 hover:text-gray-800"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  onClick={() => setEditingHashtags(true)}
                  className="text-blue-600 cursor-pointer hover:bg-white rounded p-1 min-h-[1.5rem] flex flex-wrap gap-1"
                >
                  {result.hashtags?.length ? (
                    result.hashtags.map((tag, i) => (
                      <span key={i} className="bg-blue-50 px-1 rounded">{tag}</span>
                    ))
                  ) : (
                    <span className="italic text-gray-400">Click to add hashtags...</span>
                  )}
                </div>
              )}
            </div>
          </details>

          {/* User Notes */}
          <details className="bg-gray-50 rounded">
            <summary className="px-2 py-1 cursor-pointer hover:bg-gray-100 font-medium text-gray-700">
              My Notes
            </summary>
            <div className="px-2 pb-2">
              {editingNotes ? (
                <div className="space-y-1">
                  <textarea
                    value={notesValue}
                    onChange={(e) => setNotesValue(e.target.value)}
                    rows={2}
                    placeholder="Add your notes..."
                    className="w-full text-xs border border-gray-300 rounded px-2 py-1"
                    autoFocus
                  />
                  <div className="flex gap-1">
                    <button
                      onClick={handleSaveNotes}
                      disabled={saving}
                      className="px-2 py-0.5 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50"
                    >
                      {saving ? '...' : 'Save'}
                    </button>
                    <button
                      onClick={() => {
                        setNotesValue(result.user_notes || '')
                        setEditingNotes(false)
                      }}
                      className="px-2 py-0.5 text-gray-600 hover:text-gray-800"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  onClick={() => setEditingNotes(true)}
                  className="text-gray-600 cursor-pointer hover:bg-white rounded p-1 min-h-[1.5rem]"
                >
                  {result.user_notes || <span className="italic text-gray-400">Click to add notes...</span>}
                </div>
              )}
            </div>
          </details>

          {/* AI Notes (read-only) */}
          {result.ai_notes && (
            <details className="bg-gray-50 rounded">
              <summary className="px-2 py-1 cursor-pointer hover:bg-gray-100 font-medium text-gray-700">
                AI Notes
              </summary>
              <div className="px-2 pb-2 text-gray-600">
                {result.ai_notes}
              </div>
            </details>
          )}

          {/* Scheduling Settings */}
          <SchedulingSettings
            result={result}
            jobId={jobId}
            updateResultInStore={updateResultInStore}
            setError={setError}
          />

          {/* Technical Info */}
          <details className="bg-gray-50 rounded">
            <summary className="px-2 py-1 cursor-pointer hover:bg-gray-100 font-medium text-gray-500">
              Technical Info
            </summary>
            <div className="px-2 pb-2 space-y-1 text-gray-500">
              <div className="flex items-center gap-1">
                <span className="font-medium">ID:</span>
                <span className="font-mono text-[10px] truncate flex-1">{result.result_id}</span>
                <CopyButton text={result.result_id} size="sm" />
              </div>
              <div>
                <span className="font-medium">Type:</span> {result.media_type}
              </div>
              {result.media_url && (
                <div className="flex items-center gap-1">
                  <span className="font-medium">URL:</span>
                  <a href={result.media_url} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline truncate flex-1">
                    View
                  </a>
                  <CopyButton text={result.media_url} size="sm" />
                </div>
              )}
              {result.tracer && (
                <div>
                  <span className="font-medium">Tracer:</span> {result.tracer}
                </div>
              )}
              {result.inbox_tracer && (
                <div>
                  <span className="font-medium">Inbox:</span> {result.inbox_tracer}
                </div>
              )}
              <div>
                <span className="font-medium">Created:</span> {new Date(result.created_at).toLocaleString()}
              </div>
            </div>
          </details>
        </div>
      </details>
    </div>
  )
}

function ScheduleModal({
  result,
  job,
  onClose,
  onSchedule,
}: {
  result: Result
  job: Job
  onClose: () => void
  onSchedule: (data: { scheduled_datetime: string; on_schedule_email?: string; instruction_text?: string }) => void
}) {
  const [datetime, setDatetime] = useState('')
  const [email, setEmail] = useState(job.on_schedule_email_default || '')
  const [instructions, setInstructions] = useState('')
  const [loading, setLoading] = useState(false)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!datetime) return

    setLoading(true)
    try {
      await onSchedule({
        scheduled_datetime: new Date(datetime).toISOString(),
        on_schedule_email: email || undefined,
        instruction_text: instructions || undefined,
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-md w-full p-6">
        <h2 className="text-lg font-semibold mb-4">Schedule Post</h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Schedule Date & Time *
            </label>
            <input
              type="datetime-local"
              value={datetime}
              onChange={(e) => setDatetime(e.target.value)}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notify Email (optional)
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="volunteer@example.com"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Instructions (optional)
            </label>
            <textarea
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              rows={3}
              placeholder="Special instructions for posting..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>

          <div className="flex gap-3 mt-6">
            <LoadingButton
              type="button"
              onClick={onClose}
              variant="secondary"
              className="flex-1"
            >
              Cancel
            </LoadingButton>
            <LoadingButton
              type="submit"
              loading={loading}
              loadingText="Scheduling..."
              disabled={!datetime}
              variant="primary"
              className="flex-1"
            >
              Schedule
            </LoadingButton>
          </div>
        </form>
      </div>
    </div>
  )
}
